import type { EscalationBand, Tone } from "../tokens/source";

export type { Tone, EscalationBand };

/**
 * (H) doc 40 §13: focus is never removed. `:focus-visible` renders the
 * `shadow-focus-ring` halo **plus** a `border-strong` edge — the halo alone is
 * a 0.12-alpha tint and does not clear 3:1 on its own, so the edge is the part
 * that actually satisfies WCAG 2.4.11. One definition, used by every control.
 */
export const focusRing =
  "outline-none focus-visible:border-strong focus-visible:shadow-focus-ring";

/**
 * For controls with no border of their own (a ghost button, a nav row): the
 * ring is drawn as an outline so there is a visible 3:1 edge either way.
 */
export const focusOutline =
  "outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-strong";

/** (H) doc 40 §10 / §14: hit targets stay >=24x24px in every density mode. */
export const minTarget = "min-h-target min-w-target";

/** doc 40 §11: hover / focus / colour transitions only, 120ms. */
export const hoverTransition =
  "transition-colors duration-hover ease-hover motion-reduce:transition-none";

/**
 * Tone -> role classes (doc 40 §4).
 *
 * This is NOT a status colour map: no status name appears here. Which tone a
 * status has comes from the generated `*_STATE_META` in `@sarva/domain-types`
 * (doc 27 §3); this table only says what a *tone* looks like. Adding a new
 * claim state to `contracts/states/*.yaml` gives it a label, a tone and a
 * treatment with no change to this file, which is the point of ADR 0008.
 *
 * The class strings are static because Tailwind cannot see a class name
 * assembled at runtime.
 */
export const TONE_CLASSES: Record<
  Tone,
  { dot: string; text: string; pill: string; border: string }
> = {
  neutral: {
    // (H) doc 40 §4: the neutral dot is gray-500 (4.83:1), never a lighter grey.
    dot: "bg-dot-neutral",
    text: "text-tertiary",
    pill: "bg-active text-secondary",
    border: "border-default",
  },
  info: {
    dot: "bg-dot-info",
    text: "text-info",
    pill: "bg-info-weak text-info",
    border: "border-info",
  },
  success: {
    dot: "bg-dot-success",
    text: "text-success",
    pill: "bg-success-weak text-success",
    border: "border-success",
  },
  warning: {
    dot: "bg-dot-warning",
    text: "text-warning",
    pill: "bg-warning-weak text-warning",
    border: "border-warning",
  },
  danger: {
    dot: "bg-dot-danger",
    text: "text-danger",
    pill: "bg-danger-weak text-danger",
    border: "border-danger",
  },
  /**
   * (H) doc 40 §5: a realised loss must not read as a warning. It is the only
   * solid-filled treatment in the system, so it differs by fill as well as by
   * hue and stays distinguishable in greyscale.
   */
  loss: {
    dot: "bg-dot-loss",
    text: "text-on-danger",
    pill: "bg-loss text-on-danger",
    border: "border-danger",
  },
};

/**
 * Escalation band -> role classes (doc 40 §5).
 *
 * `row` is (H) doc 41 §5: a queue row carries its band as a **left border**
 * or a `bg-*-weak` tint. Never a weight change — mixed weights down a column
 * destroy the vertical rhythm that makes scanning possible.
 *
 * (H) The LEFT border belongs to escalation alone. Selection takes a
 * background tint and the keyboard cursor takes a top+bottom rule, so a
 * selected urgent row keeps all three signals — see `DataTable`.
 */
export const BAND_CLASSES: Record<
  EscalationBand,
  { chip: string; dot: string; row: string }
> = {
  0: {
    // Band 0 is deliberately un-styled: comfortable gets no emphasis at all.
    chip: "text-tertiary",
    dot: "bg-escalation-dot-0",
    row: "",
  },
  1: {
    chip: "bg-escalation-1 text-escalation-1",
    dot: "bg-escalation-dot-1",
    row: "",
  },
  2: {
    chip: "bg-escalation-2 text-escalation-2",
    dot: "bg-escalation-dot-2",
    row: "",
  },
  3: {
    chip: "bg-escalation-3 text-escalation-3",
    dot: "bg-escalation-dot-3",
    row: "border-l-2 border-l-escalation-3",
  },
  4: {
    chip: "bg-escalation-4 text-escalation-4",
    dot: "bg-escalation-dot-4",
    row: "border-l-2 border-l-escalation-4",
  },
  5: {
    // (H) doc 40 §5: the only solid fill in the system.
    chip: "bg-escalation-5 text-escalation-5",
    dot: "bg-escalation-dot-5",
    row: "border-l-2 border-l-escalation-5",
  },
};

/**
 * The card recipe (doc 40 §1, §6, §7): a white surface, a 1px warm-grey card
 * edge, an 8px radius, and almost no shadow. (H) A table row, a list item and
 * an inline panel take the border and NOT the shadow.
 */
export const cardSurface = "rounded-lg border border-default bg-default";
export const cardElevated = `${cardSurface} shadow-card`;
